"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var Validators_1 = require("./Validators");
var ValidationTranslations_1 = require("./ValidationTranslations/ValidationTranslations");
var types_1 = require("../../types");
var utils_1 = require("../../utils");
var FieldsetValidationHandler_1 = require("./FieldsetValidationHandler");
// eslint-disable-next-line complexity
function isValidElement(element) {
    if (element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLFieldSetElement) {
        if (!element.id) {
            console.error('Element is missing id. Element with validators requires an id attribute.', element);
        }
        return true;
    }
    else {
        return false;
    }
}
exports.isValidElement = isValidElement;
var ValidationServiceImpl = /** @class */ (function () {
    function ValidationServiceImpl() {
        this.validationErrorMessages = {};
        this.validationStates = {};
        this.elementValidatorsReferences = {};
        this.addValidationErrorMessages(ValidationTranslations_1.getErrorMessages());
    }
    ValidationServiceImpl.prototype.getElementsAndValidators = function () {
        return this.elementValidatorsReferences;
    };
    Object.defineProperty(ValidationServiceImpl.prototype, "isAnyTouched", {
        get: function () {
            return Object.values(this.validationStates).some(function (item) { return item.touched; });
        },
        enumerable: true,
        configurable: true
    });
    ValidationServiceImpl.prototype.addValidationErrorMessages = function (validationErrorMessages) {
        this.validationErrorMessages = __assign(__assign({}, this.validationErrorMessages), validationErrorMessages);
    };
    ValidationServiceImpl.prototype.registerValidator = function (validator) {
        Validators_1.validators[validator.name] = validator;
    };
    ValidationServiceImpl.prototype.hasExistingReference = function (elementValidatorsReference, element) {
        return utils_1.isDefined(elementValidatorsReference) && elementValidatorsReference.element === element;
    };
    ValidationServiceImpl.prototype.shouldApplyNewConfigOnBaseConfig = function (isBaseConfigs, elementValidatorsReference) {
        return !isBaseConfigs && utils_1.isDefined(elementValidatorsReference.baseValidatorConfigs);
    };
    ValidationServiceImpl.prototype.decideEffectiveValidatorReference = function (element, newValidatorConfigs, isBaseConfigs) {
        var elementValidatorsReference = this.elementValidatorsReferences[element.id];
        var hasExistingReference = this.hasExistingReference(elementValidatorsReference, element);
        var validatorConfigs;
        if (!hasExistingReference) {
            validatorConfigs = newValidatorConfigs;
        }
        else if (this.shouldApplyNewConfigOnBaseConfig(isBaseConfigs, elementValidatorsReference)) {
            validatorConfigs = this.mergeValidatorConfigs(elementValidatorsReference.baseValidatorConfigs, newValidatorConfigs);
        }
        else if (isBaseConfigs) {
            validatorConfigs = this.mergeValidatorConfigs(newValidatorConfigs, elementValidatorsReference.validatorConfigs);
            elementValidatorsReference.baseValidatorConfigs = newValidatorConfigs;
        }
        else {
            validatorConfigs = newValidatorConfigs;
        }
        return validatorConfigs;
    };
    /*
     * isBaseConfigs: is a boolean which indicates if this configuration should always lay as a base configuration even if overlaying
     * code is changing its validators. //#endregion
     * As an example there is FEmailTextField which sets its own EmailValidator in the constructor. Then the app-code can add its own
     * validatorsConfig on top of that (example: required: { enabled: $someValue }). Setting isBaseConfigs to true preserves the
     * validatorConfig set by FEmailTextField as the app code changes the required validatorConfig
     * (which triggers addValidatorsToElement() again)
     */
    ValidationServiceImpl.prototype.addValidatorsToElement = function (element, newValidatorConfigs, isBaseConfigs) {
        if (isBaseConfigs === void 0) { isBaseConfigs = false; }
        if (!isValidElement(element)) {
            return;
        }
        var validatorConfigs = this.decideEffectiveValidatorReference(element, newValidatorConfigs, isBaseConfigs);
        if (element instanceof HTMLFieldSetElement) {
            // eslint-disable-next-line no-new
            new FieldsetValidationHandler_1.FieldsetValidationHandler(element);
        }
        this.setRequiredAttribute(element, validatorConfigs);
        var foundValidators = this.getValidators(validatorConfigs);
        var useInstantValidation = this.useInstantValidation(foundValidators, validatorConfigs);
        var elementValidatorsReference = this.elementValidatorsReferences[element.id];
        if (this.hasExistingReference(elementValidatorsReference, element)) {
            elementValidatorsReference.validatorConfigs = validatorConfigs;
            elementValidatorsReference.validators = foundValidators;
            elementValidatorsReference.instant = useInstantValidation;
        }
        else {
            elementValidatorsReference = {
                validators: foundValidators,
                validatorConfigs: validatorConfigs,
                element: element,
                instant: useInstantValidation,
                baseValidatorConfigs: isBaseConfigs ? validatorConfigs : undefined,
            };
            this.elementValidatorsReferences[element.id] = elementValidatorsReference;
            this.createEventListeners(elementValidatorsReference);
        }
    };
    ValidationServiceImpl.prototype.mergeValidatorConfigs = function (config1, config2) {
        var mergedConfig = __assign({}, config1);
        Object.keys(config2).forEach(function (key) { return (mergedConfig[key] = __assign(__assign({}, mergedConfig[key]), config2[key])); });
        return mergedConfig;
    };
    ValidationServiceImpl.prototype.createEventListeners = function (elementValidatorsReference) {
        var _this = this;
        var element = elementValidatorsReference.element;
        if (!(element instanceof HTMLFieldSetElement)) {
            element.addEventListener('input', function () {
                var validationState = _this.getExistingStateOrSetDefault(element);
                validationState.serverError = undefined;
                if (elementValidatorsReference.instant) {
                    validationState.touched = true;
                    _this.validateAndDispatchEvent({
                        validationState: validationState,
                        elementValidatorsReference: elementValidatorsReference,
                    });
                }
                else {
                    element.dispatchEvent(new CustomEvent('pending-validity', {
                        bubbles: false,
                    }));
                }
            });
            element.addEventListener('change', function () {
                var validationState = _this.getExistingStateOrSetDefault(element);
                validationState.touched = true;
                _this.validateAndDispatchEvent({ validationState: validationState, elementValidatorsReference: elementValidatorsReference, formatValue: true });
            });
            element.addEventListener('blur', function () {
                var validationState = _this.getExistingStateOrSetDefault(element);
                validationState.touched = true;
                _this.validateAndDispatchEvent({ validationState: validationState, elementValidatorsReference: elementValidatorsReference, formatValue: true });
            });
        }
        element.addEventListener('validate', function () {
            var validationState = _this.getExistingStateOrSetDefault(element);
            _this.validateAndDispatchEvent({ validationState: validationState, elementValidatorsReference: elementValidatorsReference });
        });
    };
    ValidationServiceImpl.prototype.validateElement = function (id) {
        var element = document.getElementById(id);
        if (element && isValidElement(element)) {
            element.dispatchEvent(new CustomEvent('validate', {
                bubbles: false,
            }));
        }
        else {
            throw new Error("Element with id '" + id + "' is not an input, textarea or select element.'");
        }
    };
    ValidationServiceImpl.prototype.validateAllElements = function (id) {
        var _this = this;
        var validatableElements = document.querySelectorAll("#" + id + " input, #" + id + " textarea, #" + id + " select, #" + id + " fieldset");
        validatableElements.forEach(function (element) {
            if (element.id) {
                _this.validateElement(element.id);
            }
        });
    };
    ValidationServiceImpl.prototype.setState = function (id, validationState) {
        var existingState = this.validationStates[id];
        if (existingState) {
            this.validationStates[id] = __assign(__assign({}, existingState), validationState);
        }
        else {
            this.validationStates[id] = validationState;
        }
    };
    /* eslint-disable max-depth */
    ValidationServiceImpl.prototype.setRequiredAttribute = function (element, validatorConfigs) {
        if (validatorConfigs.required) {
            if (validatorConfigs.required.enabled !== false) {
                element.setAttribute('data-required', '');
                if (utils_1.isValidatableFormElement(element)) {
                    element.setAttribute('required', '');
                }
            }
            else {
                element.removeAttribute('data-required');
                if (utils_1.isValidatableFormElement(element)) {
                    element.removeAttribute('required');
                }
            }
        }
    };
    /* eslint-enable max-depth */
    ValidationServiceImpl.prototype.useInstantValidation = function (foundValidators, validatorConfigs) {
        return foundValidators.some(function (validator) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            var instantConfig = utils_1.isDefined(validatorConfigs[validator.name]) ? validatorConfigs[validator.name].instant : undefined;
            return (validator.instant && instantConfig !== false) || instantConfig === true;
        });
    };
    ValidationServiceImpl.prototype.getState = function (id) {
        return this.validationStates[id];
    };
    ValidationServiceImpl.prototype.getExistingStateOrSetDefault = function (element) {
        var validationState = this.getState(element.id);
        if (!validationState) {
            validationState = { touched: false, submitted: false };
            this.setState(element.id, validationState);
        }
        return validationState;
    };
    ValidationServiceImpl.prototype.getValidators = function (validatorConfigs) {
        var validatorNames = Object.keys(validatorConfigs);
        return validatorNames.map(function (validatorName) {
            var validator = Validators_1.validators[validatorName];
            if (validator) {
                return validator;
            }
            throw new Error("Validator '" + validatorName + "' does not exists or is not registered, See ValidatorService.registerValidator.");
        });
    };
    ValidationServiceImpl.prototype.validateAndDispatchEvent = function (params) {
        var validationResult = this.validateAll(params.elementValidatorsReference.element, params.validationState, params.elementValidatorsReference.validators, params.elementValidatorsReference.validatorConfigs);
        var validityMode = this.resolveValidityMode(params.elementValidatorsReference.element, params.validationState, validationResult.isValid);
        if (validationResult.isValid && params.formatValue) {
            this.formatValue(params.elementValidatorsReference.element, params.elementValidatorsReference.validators);
        }
        this.dispatchValidityEvent(params.elementValidatorsReference.element, __assign(__assign({}, validationResult), { target: params.elementValidatorsReference.element, elementId: params.elementValidatorsReference.element.id, validityMode: validityMode }));
    };
    ValidationServiceImpl.prototype.resolveValidityMode = function (element, validationState, isValid) {
        if (validationState.serverError) {
            return 'ERROR';
        }
        else if (isValid) {
            return this.resolveValidityModeWhenValid(element);
        }
        else {
            return this.resolveValidityModeWhenError(element, validationState.touched, validationState.submitted);
        }
    };
    ValidationServiceImpl.prototype.resolveValidityModeWhenValid = function (element) {
        return this.hasValue(element) ? 'VALID' : 'INITIAL';
    };
    ValidationServiceImpl.prototype.resolveValidityModeWhenError = function (element, touched, submitted) {
        return touched || submitted || (!touched && this.hasValue(element)) ? 'ERROR' : 'INITIAL';
    };
    ValidationServiceImpl.prototype.hasValue = function (element) {
        var _this = this;
        if (element instanceof HTMLFieldSetElement) {
            return Array.from(element.querySelectorAll('input')).some(function (fieldsetInputElement) {
                return _this.hasValue(fieldsetInputElement);
            });
        }
        return Boolean(utils_1.isRadiobuttonOrCheckbox(element) ? element.checked : element.value);
    };
    ValidationServiceImpl.prototype.getValue = function (element) {
        return element instanceof HTMLFieldSetElement || !element.value ? '' : element.value.trim();
    };
    ValidationServiceImpl.prototype.validateAll = function (element, validationState, validators, validatorConfigs) {
        var _this = this;
        if (validationState.serverError) {
            return { isValid: false, validationMessage: validationState.serverError };
        }
        var valueHolder = new types_1.Reference({ value: this.getValue(element) });
        var resultFromValidators = validators.map(function (validator) {
            return { isValid: _this.validate(valueHolder, element, validator, validatorConfigs), name: validator.name };
        });
        var firstInvalidResult = resultFromValidators.find(function (result) { return !result.isValid; });
        if (firstInvalidResult) {
            var validationMessage = this.getErrorMessage(firstInvalidResult.name, validators, validatorConfigs);
            return { isValid: false, validationMessage: validationMessage };
        }
        else {
            return { isValid: true, validationMessage: '' };
        }
    };
    // eslint-disable-next-line complexity
    ValidationServiceImpl.prototype.validate = function (valueHolder, element, validator, validatorConfigs) {
        var validatorConfig = validatorConfigs[validator.name] || {};
        var isEnabled = validatorConfig.enabled === undefined || validatorConfig.enabled === true;
        /**
         * Only execute validation method if enabled is undefined or true.
         */
        var isValid = isEnabled ? validator.validation(valueHolder.ref.value, element, validatorConfig) : true;
        if (isValid && isEnabled && validator.formatter) {
            valueHolder.ref.value = validator.formatter(valueHolder.ref.value);
        }
        return isValid;
    };
    ValidationServiceImpl.prototype.formatValue = function (element, validators) {
        if (element instanceof HTMLFieldSetElement) {
            return;
        }
        element.value = element.value ? element.value.trim() : '';
        validators.forEach(function (result) {
            if (result.formatter) {
                element.value = result.formatter(element.value);
            }
        });
        element.dispatchEvent(new Event('input', { bubbles: true }));
    };
    ValidationServiceImpl.prototype.getErrorMessage = function (validatorName, validators, validatorConfigs) {
        var _this = this;
        var validatorConfig = validatorConfigs[validatorName];
        if (validatorConfig && validatorConfig.errorMessage) {
            return validatorConfig.errorMessage;
        }
        var subErrorCodeKey = validators
            .map(function (validator) { return validator.name; })
            .find(function (name) { return _this.validationErrorMessages[validatorName + "." + name] !== undefined; });
        var errorMessage = this.validationErrorMessages[validatorName];
        var combinedErrorMessage = this.validationErrorMessages[validatorName + "." + subErrorCodeKey];
        if (utils_1.isDefined(combinedErrorMessage)) {
            return combinedErrorMessage;
        }
        else if (utils_1.isDefined(errorMessage)) {
            return errorMessage;
        }
        return validatorName.toUpperCase();
    };
    ValidationServiceImpl.prototype.dispatchValidityEvent = function (element, validityEvent) {
        element.setCustomValidity(validityEvent.validationMessage);
        var elements = [];
        if (element instanceof HTMLFieldSetElement) {
            element.querySelectorAll('input').forEach(function (childElement) { return elements.push(childElement); });
        }
        else {
            elements.push(element);
        }
        var validField = validityEvent.validityMode === 'ERROR';
        elements.forEach(function (element) { return element.setAttribute('aria-invalid', validField.toString()); });
        element.dispatchEvent(new CustomEvent('validity', {
            bubbles: true,
            detail: validityEvent,
        }));
    };
    ValidationServiceImpl.prototype.clearAllStates = function () {
        this.validationStates = {};
    };
    return ValidationServiceImpl;
}());
exports.ValidationService = new ValidationServiceImpl();
// ? Needed for Styleguidist to pickup the documentation.
exports.default = {};
//# sourceMappingURL=ValidationService.js.map